import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { adminService } from '../../services/adminService';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';


const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState(null);
  const [recentUsers, setRecentUsers] = useState([]);
  const [activeBlogArticles, setActiveBlogArticles] = useState([]);
  const [pendingRedemptions, setPendingRedemptions] = useState([]);
  const [retentionStats, setRetentionStats] = useState(null);

  // Check admin access
  const isAdmin = (user?.email === 'demo@rovynbudget.com' || user?.email === 'rovyngroupinc@gmail.com') && userProfile?.role === 'admin';

  useEffect(() => {
    if (userProfile && !isAdmin) {
      navigate('/dashboard');
    }
  }, [userProfile, isAdmin, navigate]);

  useEffect(() => {
    if (isAdmin) {
      loadDashboardData();
    }
  }, [isAdmin]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [metricsData, usersData, articlesData, redemptionsData, retentionData] = await Promise.all([
        adminService?.getPlatformMetrics(),
        adminService?.getRecentRegistrations(10),
        adminService?.getActiveBlogArticles(5),
        adminService?.getPendingRedemptions(10),
        adminService?.getUserRetentionStats()
      ]);

      setMetrics(metricsData);
      setRecentUsers(usersData);
      setActiveBlogArticles(articlesData);
      setPendingRedemptions(redemptionsData);
      setRetentionStats(retentionData);
    } catch (error) {
      console.error('Error loading admin dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRedemptionAction = async (redemptionId, action) => {
    try {
      await adminService?.updateRedemptionStatus(redemptionId, action);
      loadDashboardData();
    } catch (error) {
      console.error('Error updating redemption:', error);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-24 px-16 md:px-24 lg:px-32 pb-32">
        <div className="max-w-[1400px] mx-auto">
          {/* Page Header */}
          <div className="mb-32 mt-24">
            <div className="flex items-center gap-12 mb-12">
              <Icon name="Shield" size={32} color="var(--color-primary)" strokeWidth={2.5} />
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-secondary">
                Admin Dashboard
              </h1>
            </div>
            <p className="text-base text-muted-foreground font-caption">
              Centralized platform oversight and management for authorized administrators
            </p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-64">
              <Icon name="Loader" size={32} color="var(--color-primary)" className="animate-spin" />
            </div>
          ) : (
            <>
              {/* Platform Metrics */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-20 mb-32">
                <div className="bg-card rounded-lg p-24 shadow-elevation-2">
                  <div className="flex items-start justify-between mb-16">
                    <div className="flex-1">
                      <h3 className="text-sm font-caption text-muted-foreground mb-8">
                        Total Active Users
                      </h3>
                      <p className="text-3xl font-heading font-bold text-primary">
                        {metrics?.totalUsers?.toLocaleString()}
                      </p>
                    </div>
                    <div className="flex items-center justify-center w-14 h-14 bg-primary/10 rounded-lg">
                      <Icon name="Users" size={24} color="var(--color-primary)" strokeWidth={2.5} />
                    </div>
                  </div>
                </div>

                <div className="bg-card rounded-lg p-24 shadow-elevation-2">
                  <div className="flex items-start justify-between mb-16">
                    <div className="flex-1">
                      <h3 className="text-sm font-caption text-muted-foreground mb-8">
                        Monthly Signups
                      </h3>
                      <p className="text-3xl font-heading font-bold text-success">
                        {metrics?.monthlySignups?.toLocaleString()}
                      </p>
                    </div>
                    <div className="flex items-center justify-center w-14 h-14 bg-success/10 rounded-lg">
                      <Icon name="UserPlus" size={24} color="var(--color-success)" strokeWidth={2.5} />
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">Last 30 days</p>
                </div>

                <div className="bg-card rounded-lg p-24 shadow-elevation-2">
                  <div className="flex items-start justify-between mb-16">
                    <div className="flex-1">
                      <h3 className="text-sm font-caption text-muted-foreground mb-8">
                        Blog Engagement
                      </h3>
                      <p className="text-3xl font-heading font-bold text-accent">
                        {metrics?.blogEngagement?.toLocaleString()}
                      </p>
                    </div>
                    <div className="flex items-center justify-center w-14 h-14 bg-accent/10 rounded-lg">
                      <Icon name="BookOpen" size={24} color="var(--color-accent)" strokeWidth={2.5} />
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">Total article views</p>
                </div>

                <div className="bg-card rounded-lg p-24 shadow-elevation-2">
                  <div className="flex items-start justify-between mb-16">
                    <div className="flex-1">
                      <h3 className="text-sm font-caption text-muted-foreground mb-8">
                        Rewards Activity
                      </h3>
                      <p className="text-3xl font-heading font-bold text-warning">
                        {metrics?.rewardsActivity?.toLocaleString()}
                      </p>
                    </div>
                    <div className="flex items-center justify-center w-14 h-14 bg-warning/10 rounded-lg">
                      <Icon name="Gift" size={24} color="var(--color-warning)" strokeWidth={2.5} />
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">Total points earned</p>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-20 mb-32">
                <button
                  onClick={() => navigate('/user-management-analytics')}
                  className="bg-card rounded-lg p-20 shadow-elevation-2 hover:shadow-elevation-3 transition-smooth text-left group"
                >
                  <div className="flex items-center gap-12 mb-12">
                    <div className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-lg group-hover:bg-primary/20 transition-smooth">
                      <Icon name="Users" size={20} color="var(--color-primary)" />
                    </div>
                    <h3 className="text-base font-heading font-semibold text-foreground">
                      User Management
                    </h3>
                  </div>
                  <p className="text-sm text-muted-foreground font-caption">
                    View and manage user accounts
                  </p>
                </button>

                <button
                  onClick={() => navigate('/blog-article-editor')}
                  className="bg-card rounded-lg p-20 shadow-elevation-2 hover:shadow-elevation-3 transition-smooth text-left group"
                >
                  <div className="flex items-center gap-12 mb-12">
                    <div className="flex items-center justify-center w-12 h-12 bg-accent/10 rounded-lg group-hover:bg-accent/20 transition-smooth">
                      <Icon name="Edit" size={20} color="var(--color-accent)" />
                    </div>
                    <h3 className="text-base font-heading font-semibold text-foreground">
                      Blog Management
                    </h3>
                  </div>
                  <p className="text-sm text-muted-foreground font-caption">
                    Create and edit blog articles
                  </p>
                </button>

                <button
                  onClick={() => navigate('/rewards-center')}
                  className="bg-card rounded-lg p-20 shadow-elevation-2 hover:shadow-elevation-3 transition-smooth text-left group"
                >
                  <div className="flex items-center gap-12 mb-12">
                    <div className="flex items-center justify-center w-12 h-12 bg-warning/10 rounded-lg group-hover:bg-warning/20 transition-smooth">
                      <Icon name="Gift" size={20} color="var(--color-warning)" />
                    </div>
                    <h3 className="text-base font-heading font-semibold text-foreground">
                      Rewards System
                    </h3>
                  </div>
                  <p className="text-sm text-muted-foreground font-caption">
                    Configure rewards and redemptions
                  </p>
                </button>

                <button
                  onClick={() => navigate('/financial-reports')}
                  className="bg-card rounded-lg p-20 shadow-elevation-2 hover:shadow-elevation-3 transition-smooth text-left group"
                >
                  <div className="flex items-center gap-12 mb-12">
                    <div className="flex items-center justify-center w-12 h-12 bg-success/10 rounded-lg group-hover:bg-success/20 transition-smooth">
                      <Icon name="BarChart" size={20} color="var(--color-success)" />
                    </div>
                    <h3 className="text-base font-heading font-semibold text-foreground">
                      Analytics Reports
                    </h3>
                  </div>
                  <p className="text-sm text-muted-foreground font-caption">
                    Generate platform analytics
                  </p>
                </button>
              </div>

              {/* Main Content Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-24">
                {/* Recent User Registrations */}
                <div className="bg-card rounded-lg p-24 shadow-elevation-2">
                  <div className="flex items-center justify-between mb-20">
                    <h2 className="text-lg font-heading font-semibold text-foreground">
                      Recent Registrations
                    </h2>
                    <button
                      onClick={() => navigate('/user-management-analytics')}
                      className="text-sm text-primary hover:text-primary/80 font-caption transition-smooth"
                    >
                      View All
                    </button>
                  </div>
                  <div className="space-y-12">
                    {recentUsers?.length > 0 ? (
                      recentUsers?.map((user) => (
                        <div
                          key={user?.id}
                          className="flex items-center justify-between p-12 rounded-lg hover:bg-muted transition-smooth"
                        >
                          <div className="flex items-center gap-12">
                            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                              <Icon name="User" size={16} color="var(--color-primary)" />
                            </div>
                            <div>
                              <p className="text-sm font-caption font-medium text-foreground">
                                {user?.fullName || 'Unknown User'}
                              </p>
                              <p className="text-xs text-muted-foreground">{user?.email}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-muted-foreground">
                              {formatDate(user?.createdAt)}
                            </p>
                            <span className="inline-block mt-4 px-8 py-2 bg-primary/10 text-primary text-xs rounded">
                              {user?.role}
                            </span>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-20">
                        No recent registrations
                      </p>
                    )}
                  </div>
                </div>

                {/* Active Blog Articles */}
                <div className="bg-card rounded-lg p-24 shadow-elevation-2">
                  <div className="flex items-center justify-between mb-20">
                    <h2 className="text-lg font-heading font-semibold text-foreground">
                      Top Blog Articles
                    </h2>
                    <button
                      onClick={() => navigate('/financial-blog')}
                      className="text-sm text-primary hover:text-primary/80 font-caption transition-smooth"
                    >
                      View All
                    </button>
                  </div>
                  <div className="space-y-12">
                    {activeBlogArticles?.length > 0 ? (
                      activeBlogArticles?.map((article) => (
                        <div
                          key={article?.id}
                          className="flex items-center justify-between p-12 rounded-lg hover:bg-muted transition-smooth"
                        >
                          <div className="flex-1">
                            <p className="text-sm font-caption font-medium text-foreground mb-4">
                              {article?.title}
                            </p>
                            <div className="flex items-center gap-8">
                              <span className="inline-block px-8 py-2 bg-success/10 text-success text-xs rounded">
                                {article?.status}
                              </span>
                              <span className="text-xs text-muted-foreground">
                                {article?.viewCount} views
                              </span>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-20">
                        No published articles
                      </p>
                    )}
                  </div>
                </div>

                {/* Pending Redemptions */}
                <div className="bg-card rounded-lg p-24 shadow-elevation-2 lg:col-span-2">
                  <div className="flex items-center justify-between mb-20">
                    <h2 className="text-lg font-heading font-semibold text-foreground">
                      Pending Reward Redemptions
                    </h2>
                    <span className="px-12 py-4 bg-warning/10 text-warning text-sm font-caption rounded">
                      {pendingRedemptions?.length} pending
                    </span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-border">
                          <th className="text-left py-12 px-8 text-sm font-caption font-semibold text-muted-foreground">
                            User
                          </th>
                          <th className="text-left py-12 px-8 text-sm font-caption font-semibold text-muted-foreground">
                            Redemption
                          </th>
                          <th className="text-left py-12 px-8 text-sm font-caption font-semibold text-muted-foreground">
                            Points
                          </th>
                          <th className="text-left py-12 px-8 text-sm font-caption font-semibold text-muted-foreground">
                            Date
                          </th>
                          <th className="text-right py-12 px-8 text-sm font-caption font-semibold text-muted-foreground">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {pendingRedemptions?.length > 0 ? (
                          pendingRedemptions?.map((redemption) => (
                            <tr key={redemption?.id} className="border-b border-border hover:bg-muted transition-smooth">
                              <td className="py-12 px-8">
                                <p className="text-sm font-caption text-foreground">
                                  {redemption?.userName || 'Unknown'}
                                </p>
                                <p className="text-xs text-muted-foreground">{redemption?.userEmail}</p>
                              </td>
                              <td className="py-12 px-8">
                                <p className="text-sm font-caption text-foreground">
                                  {redemption?.redemptionTitle}
                                </p>
                              </td>
                              <td className="py-12 px-8">
                                <p className="text-sm font-caption font-medium text-warning">
                                  {redemption?.pointsSpent?.toLocaleString()}
                                </p>
                              </td>
                              <td className="py-12 px-8">
                                <p className="text-sm font-caption text-muted-foreground">
                                  {formatDate(redemption?.createdAt)}
                                </p>
                              </td>
                              <td className="py-12 px-8 text-right">
                                <div className="flex items-center justify-end gap-8">
                                  <button
                                    onClick={() => handleRedemptionAction(redemption?.id, 'completed')}
                                    className="px-12 py-6 bg-success/10 text-success text-xs font-caption rounded hover:bg-success/20 transition-smooth"
                                  >
                                    Approve
                                  </button>
                                  <button
                                    onClick={() => handleRedemptionAction(redemption?.id, 'cancelled')}
                                    className="px-12 py-6 bg-error/10 text-error text-xs font-caption rounded hover:bg-error/20 transition-smooth"
                                  >
                                    Reject
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="5" className="py-20 text-center">
                              <p className="text-sm text-muted-foreground">No pending redemptions</p>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* User Retention Stats */}
                {retentionStats && (
                  <div className="bg-card rounded-lg p-24 shadow-elevation-2 lg:col-span-2">
                    <h2 className="text-lg font-heading font-semibold text-foreground mb-20">
                      User Retention Analytics
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-20">
                      <div className="p-16 bg-muted rounded-lg">
                        <p className="text-sm text-muted-foreground font-caption mb-8">Cohort Size</p>
                        <p className="text-2xl font-heading font-bold text-foreground">
                          {retentionStats?.cohortSize}
                        </p>
                        <p className="text-xs text-muted-foreground mt-4">Users from 30-60 days ago</p>
                      </div>
                      <div className="p-16 bg-muted rounded-lg">
                        <p className="text-sm text-muted-foreground font-caption mb-8">Active Users</p>
                        <p className="text-2xl font-heading font-bold text-success">
                          {retentionStats?.activeUsers}
                        </p>
                        <p className="text-xs text-muted-foreground mt-4">Active in last 30 days</p>
                      </div>
                      <div className="p-16 bg-muted rounded-lg">
                        <p className="text-sm text-muted-foreground font-caption mb-8">Retention Rate</p>
                        <p className="text-2xl font-heading font-bold text-primary">
                          {retentionStats?.retentionRate}%
                        </p>
                        <p className="text-xs text-muted-foreground mt-4">30-day retention</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;